import { createContext, PropsWithChildren, useContext, useEffect, useState } from 'react';
import { ConfigProvider as AntdConfigProvider, theme, ThemeConfig } from 'antd';
import { ThemeProvider as AntDThemeProvider } from 'antd-style';
import { RefineThemes } from '@refinedev/antd';
import './config.css';

type Mode = 'light' | 'dark';

type ThemeContextType = {
  mode: Mode;
  setMode: (mode: Mode) => void;
};

export const ThemeContext = createContext({} as ThemeContextType);

const defaultMode = (localStorage.getItem('theme') as Mode) || 'light';

type ThemeProviderProps = PropsWithChildren<{
  theme?: ThemeConfig;
}>;

export const ThemeProvider = ({ theme: themeFromProps, children }: ThemeProviderProps) => {
  const [mode, setMode] = useState<Mode>(defaultMode);

  const handleSetMode = (mode: Mode) => {
    localStorage.setItem('theme', mode);
    const html = document.querySelector('html');
    html?.setAttribute('data-theme', mode);
    setMode(mode);
  };

  // add data-theme to html tag
  useEffect(() => {
    const html = document.querySelector('html');
    html?.setAttribute('data-theme', mode);
  }, []);

  return (
    <ThemeContext.Provider value={{ mode, setMode: handleSetMode }}>
      <AntdConfigProvider
        theme={{
          ...RefineThemes.Orange,
          algorithm: mode === 'light' ? theme.defaultAlgorithm : theme.darkAlgorithm,
          ...themeFromProps,
        }}
      >
        <AntDThemeProvider appearance={mode}>{children}</AntDThemeProvider>
      </AntdConfigProvider>
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);

  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }

  return context;
};
